/**
 * Print Scorecard chapter — Canva page-06 Four Areas This Month.
 * Scorecard overview chart, Canva-style 2×2 area cards, Protect banner.
 */

import React from "react";
import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  Briefcase,
  Coins,
  Heart,
  Link2,
  Minus,
  Shield,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import type { MonthlyConsultationBundle } from "../../../../utils/forecast/monthlyConsultation";
import { BarGroup } from "../../shared/BarGroup";
import { PdfChapterShell } from "../atoms";
import {
  SCORECARD_OVERVIEW_BLURB,
  SCORECARD_PAGE_INTRO,
  SCORECARD_PROTECT_INTRO,
  SCORECARD_TRENDS_BLURB,
  protectBannerBody,
  scorecardTrendCue,
} from "../pdfCopy";
import {
  bodyFontFamily,
  border,
  cardShadow,
  cardWhite,
  charcoal,
  displayFontFamily,
  gold,
  insightDark,
  mutedGray,
  paleTan,
  tanGold,
} from "../pdfTheme";

/**
 * Props for the PDF scorecard chapter.
 */
export interface PdfChapterScorecardProps {
  /** Full monthly consultation payload. */
  bundle: MonthlyConsultationBundle;
  /** 1-based sequential page index among rendered sheets. */
  pageNumber?: number;
}

/**
 * Trend display for a scorecard row.
 */
interface AreaTrendMeta {
  /** rising / easing / steady */
  readonly word: string;
  /** Arrow glyph */
  readonly arrow: string;
  /** Theme color for the trend line */
  readonly color: string;
  /** Lucide icon for the right-side glyph */
  readonly Icon: LucideIcon;
}

/**
 * Trend arrow meta for a scorecard row.
 *
 * @param trend - up | down | flat
 * @returns Display word, arrow, color, and icon
 */
function trendMeta(trend: "up" | "down" | "flat"): AreaTrendMeta {
  if (trend === "up") {
    return { word: "rising", arrow: "↑", color: tanGold, Icon: TrendingUp };
  }
  if (trend === "down") {
    return { word: "easing", arrow: "↓", color: tanGold, Icon: TrendingDown };
  }
  return { word: "steady", arrow: "→", color: mutedGray, Icon: Minus };
}

/**
 * Life-area icon for a scorecard label (Canva left circle).
 *
 * @param label - Career / Wealth / Relationships / Health
 * @returns Lucide icon component
 */
function areaIcon(label: string): LucideIcon {
  const key = typeof label === "string" ? label.trim().toLowerCase() : "";
  if (key.includes("career") || key.includes("work")) {
    return Briefcase;
  }
  if (key.includes("wealth") || key.includes("money")) {
    return Coins;
  }
  if (key.includes("relation") || key.includes("people")) {
    return Users;
  }
  if (key.includes("health") || key.includes("body")) {
    return Heart;
  }
  return BarChart3;
}

/**
 * Soft tint from a 6-digit hex (12% alpha suffix).
 *
 * @param color - Theme hex
 * @returns Tint suitable for icon wells
 */
function tintHex(color: string): string {
  if (/^#[0-9a-fA-F]{6}$/.test(color)) {
    return `${color}22`;
  }
  return `${mutedGray}22`;
}

/**
 * Four areas this month — one A4 sheet of bars, trend cards, and protect.
 */
export const PdfChapterScorecard: React.FC<PdfChapterScorecardProps> = ({
  bundle,
  pageNumber,
}) => {
  const rows = Array.isArray(bundle.scorecard) ? bundle.scorecard : [];
  const protect = protectBannerBody(
    typeof bundle.scorecardProtectPress === "string"
      ? bundle.scorecardProtectPress
      : ""
  );

  return (
    <PdfChapterShell
      chapterId="scorecard"
      eyebrow="Timing layer sync"
      title="Four areas this month"
      pageNumber={pageNumber}
      compact
      fillPage={true}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
          flex: "1 1 auto",
          minHeight: 0,
          height: "100%",
          fontFamily: bodyFontFamily,
          justifyContent: "space-between",
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: 12,
            lineHeight: 1.5,
            color: mutedGray,
            flexShrink: 0,
          }}
        >
          {SCORECARD_PAGE_INTRO}
        </p>

        {rows.length > 0 ? (
          <div
            className="mc-pdf-card"
            style={{
              background: cardWhite,
              border: `1px solid ${border}`,
              borderRadius: 16,
              boxShadow: cardShadow,
              padding: "14px 16px",
              boxSizing: "border-box",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                marginBottom: 10,
              }}
            >
              <div
                aria-hidden="true"
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: tintHex(tanGold),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <BarChart3 size={15} color={tanGold} strokeWidth={2} />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <p
                  style={{
                    margin: 0,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: tanGold,
                  }}
                >
                  Scorecard overview
                </p>
                <p
                  style={{
                    margin: "4px 0 0",
                    fontSize: 11,
                    lineHeight: 1.45,
                    color: mutedGray,
                  }}
                >
                  {SCORECARD_OVERVIEW_BLURB}
                </p>
              </div>
            </div>
            <BarGroup rows={rows} bare plotHeight={220} />
          </div>
        ) : (
          <p style={{ margin: 0, fontSize: 12, color: mutedGray }}>
            No scorecard rows are available for this month.
          </p>
        )}

        {rows.length > 0 ? (
          <div style={{ flexShrink: 0 }}>
            <p
              className="mc-pdf-section-rule"
              style={{
                margin: "0 0 6px",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: tanGold,
              }}
            >
              Area trends
            </p>
            <p
              style={{
                margin: "0 0 10px",
                fontSize: 11,
                lineHeight: 1.45,
                color: mutedGray,
              }}
            >
              {SCORECARD_TRENDS_BLURB}
            </p>
            <div
              className="mc-pdf-grid-2x2 mc-pdf-scorecard-areas"
              style={{ flex: "0 0 auto" }}
            >
              {rows.map((row) => {
                const trend = trendMeta(row.trendVsPrior);
                const Icon = areaIcon(row.label);
                const TrendIcon = trend.Icon;
                const label =
                  typeof row.label === "string" ? row.label.trim() : "";
                const DecoIcon =
                  label.toLowerCase().includes("wealth")
                    ? Coins
                    : label.toLowerCase().includes("relation")
                      ? Link2
                      : TrendIcon;
                const cue = scorecardTrendCue(row.trendVsPrior);

                return (
                  <div
                    key={label.length > 0 ? label : `area-${trend.word}`}
                    className="mc-pdf-card mc-pdf-scorecard-area-card"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      background: cardWhite,
                      border: `1px solid ${border}`,
                      borderRadius: 16,
                      boxShadow: cardShadow,
                      padding: "12px 14px",
                      boxSizing: "border-box",
                      fontFamily: bodyFontFamily,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <div
                        aria-hidden="true"
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          background: paleTan,
                          border: `1px solid ${border}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon size={16} color={tanGold} strokeWidth={2} />
                      </div>
                      <p
                        style={{
                          margin: 0,
                          flex: 1,
                          minWidth: 0,
                          fontFamily: displayFontFamily,
                          fontSize: 15,
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: charcoal,
                        }}
                      >
                        {label}
                      </p>
                      <DecoIcon
                        size={16}
                        color={trend.color}
                        strokeWidth={2}
                        aria-hidden="true"
                      />
                    </div>
                    <p
                      style={{
                        margin: 0,
                        fontFamily: displayFontFamily,
                        fontSize: 18,
                        fontWeight: 700,
                        lineHeight: 1.15,
                        color: trend.color,
                        paddingLeft: 46,
                      }}
                    >
                      {`${trend.arrow} ${trend.word}`}
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: 10,
                        lineHeight: 1.4,
                        color: mutedGray,
                        paddingLeft: 46,
                      }}
                    >
                      {cue}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {protect.length > 0 ? (
          <aside
            className="mc-pdf-dark-callout mc-pdf-protect-banner"
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              gap: 12,
              background: insightDark,
              borderRadius: 16,
              padding: "14px 16px",
              boxSizing: "border-box",
              width: "100%",
              fontFamily: bodyFontFamily,
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <Shield
              size={88}
              color="#ffffff"
              strokeWidth={1.2}
              aria-hidden="true"
              style={{
                position: "absolute",
                right: 12,
                bottom: -10,
                opacity: 0.08,
                pointerEvents: "none",
              }}
            />
            <div
              aria-hidden="true"
              style={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Shield size={16} color="#ffffff" strokeWidth={2} />
            </div>
            <div style={{ minWidth: 0, flex: 1, position: "relative", zIndex: 1 }}>
              <p
                style={{
                  margin: 0,
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: gold,
                }}
              >
                Protect this month
              </p>
              <p
                style={{
                  margin: "4px 0 0",
                  fontSize: 11,
                  lineHeight: 1.4,
                  color: "#d6c4a0",
                }}
              >
                {SCORECARD_PROTECT_INTRO}
              </p>
              <p
                style={{
                  margin: "6px 0 0",
                  fontSize: 12,
                  lineHeight: 1.45,
                  color: "#f5f0e8",
                }}
              >
                {protect}
              </p>
            </div>
          </aside>
        ) : null}
      </div>
    </PdfChapterShell>
  );
};

export default PdfChapterScorecard;
